import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { supabase, isConfigured } from '../lib/supabase.js'
import { track, ttrack } from '../lib/analytics.js'
import { getUtms, getVisit, deviceType } from '../lib/utm.js'

const AuthContext = createContext(null)

const NOT_CONFIGURED = {
  error: 'Backend not connected yet. Add your Supabase keys to .env.local (see README) and restart.',
}

// Map a Supabase auth user to the shape the app UI expects.
function mapUser(u) {
  if (!u) return null
  const name = u.user_metadata?.name || u.email?.split('@')[0] || 'there'
  return { id: u.id, email: u.email, name }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [initializing, setInitializing] = useState(true)

  useEffect(() => {
    if (!isConfigured) {
      setInitializing(false)
      return
    }
    let active = true

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session)
      setInitializing(false)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession)
    })

    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const user = useMemo(() => mapUser(session?.user), [session])

  async function register({ name, email, password, marketingOptIn = false }) {
    if (!isConfigured) return NOT_CONFIGURED
    const clean = email.trim().toLowerCase()
    // Acquisition context — captured client-side (only the browser knows the
    // referrer/UTMs/device). Stored in metadata so Slack alerts + admin stats can
    // read it later. You can only compute stats on what you record at signup.
    const utms = getUtms()
    const visit = getVisit()
    const { data, error } = await supabase.auth.signUp({
      email: clean,
      password,
      options: {
        data: {
          name: name?.trim() || '',
          marketing_opt_in: !!marketingOptIn,
          device: deviceType(),
          referrer: visit.referrer || '',
          landing_page: visit.landing || '',
          utm_source: utms.utm_source || '',
          utm_medium: utms.utm_medium || '',
          utm_campaign: utms.utm_campaign || '',
          utm_content: utms.utm_content || '',
          utm_term: utms.utm_term || '',
          agreed_research_use: true,
          agreed_age: true,
          agreed_at: new Date().toISOString(),
        },
      },
    })
    if (error) return { error: error.message }
    // If they opted into sponsor emails, record the lead (best-effort; ignores
    // a missing `leads` table).
    if (marketingOptIn) {
      try {
        await supabase.from('leads').insert({ email: clean, source: 'signup' })
      } catch {
        /* leads table optional */
      }
    }
    track('sign_up', { method: 'email' }) // GA4: mark as a conversion
    // TikTok Pixel conversion. event_id = the new user's id so it dedupes against
    // the server-side Events API event (send-welcome.mjs) that uses the same id.
    ttrack('CompleteRegistration', {}, { event_id: data.user?.id })
    // If the project requires email confirmation, there's no session yet.
    if (!data.session) return { needsConfirmation: true }
    return { user: mapUser(data.user) }
  }

  async function login({ email, password }) {
    if (!isConfigured) return NOT_CONFIGURED
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    })
    if (error) return { error: error.message }
    track('login', { method: 'email' })
    return { user: mapUser(data.user) }
  }

  async function logout() {
    if (!isConfigured) return
    await supabase.auth.signOut()
  }

  // Send a password-reset email. The link lands on /reset-password, where
  // detectSessionInUrl establishes a short-lived recovery session.
  async function requestPasswordReset(email) {
    if (!isConfigured) return NOT_CONFIGURED
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) return { error: error.message }
    return { ok: true }
  }

  // Set a new password for the user in the current (recovery or logged-in) session.
  async function updatePassword(password) {
    if (!isConfigured) return NOT_CONFIGURED
    const { error } = await supabase.auth.updateUser({ password })
    if (error) return { error: error.message }
    return { ok: true }
  }

  const value = { user, initializing, isConfigured, register, login, logout, requestPasswordReset, updatePassword }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => useContext(AuthContext)
